import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../utils/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import ScormStats from './ScormStats';
import './ScormManagement.css';

const ScormManagement = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    course_level: '',
    duration: '',
    passing_score: 80,
    max_attempts: 0,
    start_date: '',
    end_date: ''
  });
  const [scormFile, setScormFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [editThumbnailFile, setEditThumbnailFile] = useState(null);
  const [viewingStats, setViewingStats] = useState(null);

  // Search and Pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filtered courses based on search
  const filteredCourses = useMemo(() => {
    if (!searchTerm.trim()) return courses;
    const lowerSearch = searchTerm.toLowerCase();
    return courses.filter(course =>
      course.title?.toLowerCase().includes(lowerSearch) ||
      course.category?.toLowerCase().includes(lowerSearch) ||
      course.course_level?.toLowerCase().includes(lowerSearch) ||
      course.scorm_version?.toLowerCase().includes(lowerSearch) ||
      course.description?.toLowerCase().includes(lowerSearch)
    );
  }, [courses, searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredCourses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCourses = filteredCourses.slice(startIndex, endIndex);

  // Reset to page 1 when search or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Export to CSV function
  const handleExportCSV = () => {
    const headers = ['ID', 'Title', 'Version', 'Category', 'Level', 'Passing Score', 'Max Attempts', 'Status', 'Created Date'];
    const csvData = filteredCourses.map(course => [
      course.scorm_course_id,
      `"${(course.title || '').replace(/"/g, '""')}"`,
      course.scorm_version,
      course.category || '-',
      course.course_level || '-',
      `${course.passing_score}%`,
      course.max_attempts,
      course.is_active ? 'Active' : 'Inactive',
      new Date(course.created_at).toLocaleDateString()
    ]);

    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `scorm_courses_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const data = await apiFetch(
        `${window._env_.API_BASE_URL}/admin/scorm/courses?include_inactive=true`
      );
      setCourses(data || []);
    } catch (error) {
      console.error('Error fetching SCORM courses:', error);
      toast.error('Failed to fetch SCORM courses');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;

    if (type === 'number') {
      if (value === '') {
        newValue = '';
      } else {
        newValue = parseFloat(value);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        toast.error('Please select a valid SCORM ZIP file');
        return;
      }
      setScormFile(file);
    }
  };

  const handleThumbnailFileChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type || !file.type.includes('png')) {
        toast.error('Please select a valid PNG image file');
        return;
      }

      // Check file extension as backup
      if (!file.name.toLowerCase().endsWith('.png')) {
        toast.error('Please select a PNG image file (.png extension)');
        return;
      }

      // Check file size (500KB limit)
      if (file.size > 500 * 1024) {
        toast.error('Thumbnail image must be less than 500KB');
        return;
      }

      // Validate image dimensions
      const img = new Image();
      img.onload = () => {
        if (img.width > 800 || img.height > 600) {
          toast.error(`Thumbnail dimensions too large. Maximum size is 800x600 pixels, got ${img.width}x${img.height} pixels`);
          return;
        }

        // Set the file if validation passes
        if (isEdit) {
          setEditThumbnailFile(file);
        } else {
          setThumbnailFile(file);
        }
      };

      img.onerror = () => {
        toast.error('Invalid image file');
      };

      img.src = URL.createObjectURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUploadProgress(0);

    if (!scormFile) {
      toast.error('Please select a SCORM package file');
      setIsSubmitting(false);
      return;
    }

    if (!formData.title.trim()) {
      toast.error('Course title is required');
      setIsSubmitting(false);
      return;
    }

    if (!formData.start_date) {
      toast.error('Start date is required');
      setIsSubmitting(false);
      return;
    }

    if (!formData.end_date) {
      toast.error('End date is required');
      setIsSubmitting(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description || '');
    formDataToSend.append('category', formData.category || '');
    formDataToSend.append('course_level', formData.course_level || '');
    formDataToSend.append('duration', formData.duration || '');
    formDataToSend.append('passing_score', formData.passing_score);
    formDataToSend.append('max_attempts', formData.max_attempts);
    formDataToSend.append('start_date', formData.start_date || '');
    formDataToSend.append('end_date', formData.end_date || '');
    formDataToSend.append('scorm_file', scormFile);

    // Add thumbnail file if provided
    if (thumbnailFile) {
      formDataToSend.append('thumbnail_file', thumbnailFile);
    }

    try {
      setUploadProgress(30);
      await apiFetch(`${window._env_.API_BASE_URL}/admin/scorm/upload`, {
        method: 'POST',
        body: formDataToSend
      });
      setUploadProgress(100);
      toast.success('SCORM course uploaded successfully!');

      // Reset form
      setFormData({
        title: '',
        description: '',
        category: '',
        course_level: '',
        duration: '',
        passing_score: 80,
        max_attempts: 0,
        start_date: '',
        end_date: ''
      });
      setScormFile(null);
      setThumbnailFile(null);
      document.getElementById('scorm-file-input').value = '';
      document.getElementById('thumbnail-file-input').value = '';

      // Refresh course list
      fetchCourses();
    } catch (error) {
      console.error('Error uploading SCORM course:', error);
      toast.error(error.message || 'Failed to upload SCORM course');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    if (!editingCourse) return;

    try {
      const formDataToSend = new FormData();

      // Add all form data - only include non-empty values
      Object.keys(editFormData).forEach(key => {
        if (editFormData[key] !== null && editFormData[key] !== undefined && editFormData[key] !== '') {
          formDataToSend.append(key, editFormData[key]);
        }
      });

      // Add thumbnail file if provided
      if (editThumbnailFile) {
        formDataToSend.append('thumbnail_file', editThumbnailFile);
      }

      await apiFetch(`${window._env_.API_BASE_URL}/admin/scorm/courses/${editingCourse.scorm_course_id}`, {
        method: 'PUT',
        body: formDataToSend
      });
      toast.success('Course updated successfully');
      setEditingCourse(null);
      setEditFormData(null);
      setEditThumbnailFile(null);
      document.getElementById('edit-thumbnail-file-input').value = '';
      fetchCourses();
    } catch (error) {
      console.error('Error updating course:', error);
      toast.error('Failed to update course');
    }
  };

  const handleEditClick = (course) => {
    setEditingCourse(course);
    setEditFormData({
      title: course.title,
      description: course.description || '',
      category: course.category || '',
      course_level: course.course_level || '',
      duration: course.duration || '',
      passing_score: course.passing_score,
      max_attempts: course.max_attempts,
      start_date: course.start_date ? course.start_date.split('T')[0] : '',
      end_date: course.end_date ? course.end_date.split('T')[0] : ''
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;
    if (type === 'number') {
      newValue = value === '' ? '' : parseFloat(value);
    }
    setEditFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleEditSelectChange = (selectedOption, { name }) => {
    setEditFormData(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleToggleActive = async (courseId, currentStatus) => {
    try {
      const formData = new FormData();
      formData.append('is_active', !currentStatus);

      await apiFetch(`${window._env_.API_BASE_URL}/admin/scorm/courses/${courseId}`, {
        method: 'PUT',
        body: formData
      });
      toast.success(`Course ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchCourses();
    } catch (error) {
      console.error('Error updating course status:', error);
      toast.error('Failed to update course status');
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this SCORM course?')) return;

    try {
      await apiFetch(`${window._env_.API_BASE_URL}/admin/scorm/courses/${courseId}`, {
        method: 'DELETE'
      });
      toast.success('SCORM course deleted successfully');
      fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  const courseLevelOptions = useMemo(
    () => [
      { value: 'Beginner', label: 'Beginner' },
      { value: 'Intermediate', label: 'Intermediate' },
      { value: 'Advanced', label: 'Advanced' }
    ],
    []
  );

  const categoryOptions = useMemo(
    () => [
      { value: 'Compliance', label: 'Compliance' },
      { value: 'Technical', label: 'Technical' },
      { value: 'Soft Skills', label: 'Soft Skills' },
      { value: 'Leadership', label: 'Leadership' },
      { value: 'Onboarding', label: 'Onboarding' },
      { value: 'Safety', label: 'Safety' },
      { value: 'Product Training', label: 'Product Training' },
      { value: 'Other', label: 'Other' }
    ],
    []
  );

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      borderColor: '#ccc',
      color: '#000'
    }),
    option: (provided) => ({
      ...provided,
      color: '#000'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#000'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#888'
    })
  };

  return (
    <div className="scorm-management-container">
      <h2 className="scorm-title">SCORM Package Management</h2>
      <p className="scorm-subtitle">Upload and manage SCORM 1.2 and SCORM 2004 compliant courses</p>

      {/* Upload Form */}
      <div className="scorm-upload-section">
        <h3>Upload New SCORM Package</h3>
        <form onSubmit={handleSubmit} className="scorm-form">
          <div className="form-row">
            <div className="form-group">
              <label>Course Title <span className="required-asterisk">*</span></label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter course title"
                required
              />
            </div>
            <div className="form-group">
              <label>SCORM Package (ZIP) <span className="required-asterisk">*</span></label>
              <input
                type="file"
                id="scorm-file-input"
                accept=".zip"
                onChange={handleFileChange}
                required
              />
              {scormFile && (
                <span className="file-name">{scormFile.name} ({(scormFile.size / 1024 / 1024).toFixed(2)} MB)</span>
              )}
            </div>
            <div className="form-group">
              <label>Course Thumbnail (PNG)</label>
              <input
                type="file"
                id="thumbnail-file-input"
                accept=".png,image/png"
                onChange={(e) => handleThumbnailFileChange(e, false)}
              />
              {thumbnailFile && (
                <span className="file-name">{thumbnailFile.name} ({(thumbnailFile.size / 1024).toFixed(1)} KB)</span>
              )}
              <small className="help-text">Optional PNG image (max 800x600px, 500KB)</small>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Course description"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <Select
                name="category"
                value={categoryOptions.find((opt) => opt.value === formData.category) || null}
                onChange={(opt) => handleSelectChange(opt, { name: 'category' })}
                options={categoryOptions}
                placeholder="Select category"
                isClearable
                styles={selectStyles}
              />
            </div>
            <div className="form-group">
              <label>Course Level</label>
              <Select
                name="course_level"
                value={courseLevelOptions.find((opt) => opt.value === formData.course_level) || null}
                onChange={(opt) => handleSelectChange(opt, { name: 'course_level' })}
                options={courseLevelOptions}
                placeholder="Select level"
                isClearable
                styles={selectStyles}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Duration</label>
              <input
                type="text"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                placeholder="e.g., 2 hours"
              />
            </div>
            <div className="form-group">
              <label>Passing Score (%) <span className="required-asterisk">*</span></label>
              <input
                type="number"
                name="passing_score"
                value={formData.passing_score}
                onChange={handleChange}
                min={0}
                max={100}
                step={1}
                required
              />
            </div>
            <div className="form-group">
              <label>Max Attempts (0 = unlimited) <span className="required-asterisk">*</span></label>
              <input
                type="number"
                name="max_attempts"
                value={formData.max_attempts}
                onChange={handleChange}
                min={0}
                required
              />
            </div>
            <div className="form-group">
              <label>Start Date <span className="required-asterisk">*</span></label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date <span className="required-asterisk">*</span></label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {uploadProgress > 0 && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
              </div>
              <span>{uploadProgress}%</span>
            </div>
          )}

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Uploading...' : 'Upload SCORM Package'}
          </button>
        </form>
      </div>

      {/* Course List */}
      <div className="scorm-courses-section">
        <div className="table-header-controls">
          <h3>Uploaded SCORM Courses</h3>
          <div className="table-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchTerm('')}
                >
                  ×
                </button>
              )}
            </div>
            <button className="export-btn" onClick={handleExportCSV}>
              📥 Export CSV
            </button>
          </div>
        </div>
        {loading ? (
          <div className="loading">Loading courses...</div>
        ) : filteredCourses.length === 0 ? (
          <div className="no-courses">
            {searchTerm ? 'No courses found matching your search' : 'No SCORM courses uploaded yet'}
          </div>
        ) : (
          <>
            <div className="scorm-courses-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Thumbnail</th>
                    <th>Title</th>
                    <th>Version</th>
                    <th>Category</th>
                    <th>Level</th>
                    <th>Passing Score</th>
                    <th>Created</th>
                    <th className="actions-header">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCourses.map((course) => (
                    <tr key={course.scorm_course_id} className={!course.is_active ? 'inactive' : ''}>
                      <td>{course.scorm_course_id}</td>
                      <td className="thumbnail-cell">
                        <div className="course-thumbnail-container">
                          {course.thumbnail_url ? (
                            <img
                              src={`${window._env_.API_BASE_URL}/scorm/thumbnail/${course.thumbnail_url}`}
                              alt={`${course.title} thumbnail`}
                              className="course-thumbnail"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="course-thumbnail-fallback"
                            style={{
                              display: course.thumbnail_url ? 'none' : 'flex'
                            }}
                          >
                            📚
                          </div>
                          <div
                            className="course-thumbnail-fallback"
                            style={{
                              display: 'none'
                            }}
                          >
                            📚
                          </div>
                        </div>
                      </td>
                      <td className="course-title-cell">
                        <span>{course.title}</span>
                        {course.description && (
                          <small className="description-preview">{course.description.substring(0, 50)}...</small>
                        )}
                      </td>
                      <td>
                        <span className={`version-badge version-${course.scorm_version.replace('.', '')}`}>
                          SCORM {course.scorm_version}
                        </span>
                      </td>
                      <td>{course.category || '-'}</td>
                      <td>{course.course_level || '-'}</td>
                      <td>{course.passing_score}%</td>
                      <td>{new Date(course.created_at).toLocaleDateString()}</td>
                      <td className="actions-cell">
                        <button
                          className="action-btn stats"
                          onClick={() => setViewingStats(course)}
                          style={{ background: '#e0f7fa', color: '#006064', marginRight: '5px' }}
                        >
                          Stats
                        </button>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEditClick(course)}
                          style={{ background: '#e3f2fd', color: '#1565c0', marginRight: '5px' }}
                        >
                          Edit
                        </button>
                        <button
                          className={`action-btn ${course.is_active ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleActive(course.scorm_course_id, course.is_active)}
                        >
                          {course.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Prev
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, currentPage - 2) + i;
                if (pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={currentPage === pageNum ? 'pagination-btn active' : 'pagination-btn'}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="items-per-page"
              >
                {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
              <span className="pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredCourses.length)} of {filteredCourses.length}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editingCourse && editFormData && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: 'white', padding: '30px', borderRadius: '12px',
            width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <h3 style={{ marginBottom: '20px' }}>Edit SCORM Course</h3>
            <form onSubmit={handleUpdateCourse} className="scorm-form">
              <div className="form-group">
                <label>Course Title <span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  name="title"
                  value={editFormData.title}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditChange}
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <Select
                    name="category"
                    value={categoryOptions.find((opt) => opt.value === editFormData.category) || null}
                    onChange={(opt) => handleEditSelectChange(opt, { name: 'category' })}
                    options={categoryOptions}
                    isClearable
                    styles={selectStyles}
                  />
                </div>
                <div className="form-group">
                  <label>Course Level</label>
                  <Select
                    name="course_level"
                    value={courseLevelOptions.find((opt) => opt.value === editFormData.course_level) || null}
                    onChange={(opt) => handleEditSelectChange(opt, { name: 'course_level' })}
                    options={courseLevelOptions}
                    isClearable
                    styles={selectStyles}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Duration</label>
                  <input
                    type="text"
                    name="duration"
                    value={editFormData.duration}
                    onChange={handleEditChange}
                  />
                </div>
                <div className="form-group">
                  <label>Passing Score (%) <span className="required-asterisk">*</span></label>
                  <input
                    type="number"
                    name="passing_score"
                    value={editFormData.passing_score}
                    onChange={handleEditChange}
                    min={0}
                    max={100}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Max Attempts <span className="required-asterisk">*</span></label>
                  <input
                    type="number"
                    name="max_attempts"
                    value={editFormData.max_attempts}
                    onChange={handleEditChange}
                    min={0}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="start_date"
                    value={editFormData.start_date}
                    onChange={handleEditChange}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="end_date"
                    value={editFormData.end_date}
                    onChange={handleEditChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Course Thumbnail (PNG)</label>
                <input
                  type="file"
                  id="edit-thumbnail-file-input"
                  accept=".png,image/png"
                  onChange={(e) => handleThumbnailFileChange(e, true)}
                />
                {editThumbnailFile && (
                  <span className="file-name">{editThumbnailFile.name} ({(editThumbnailFile.size / 1024).toFixed(1)} KB)</span>
                )}
                <small className="help-text">Optional PNG image (max 800x600px, 500KB)</small>
              </div>

              <div className="form-actions" style={{ display: 'flex', gap: '15px', marginTop: '20px', justifyContent: 'flex-end' }}>
                <button type="button" className="submit-btn" style={{ background: '#666' }} onClick={() => setEditingCourse(null)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Update Course
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {viewingStats && (
        <ScormStats
          course={viewingStats}
          onClose={() => setViewingStats(null)}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default ScormManagement;

